import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { previewMonthlyReport, sendMonthlyReport } from '../lib/api';
import { toast } from 'sonner';
import {
  FileBarChart, Send, Eye, Loader2, Ship, Package,
  TrendingUp, Calendar, Award, ShieldCheck
} from 'lucide-react';

const STATUS_COLORS = {
  NO_SALIDO_CHINA: 'bg-zinc-500/20 text-zinc-300',
  EN_TRANSITO_PUERTO: 'bg-blue-500/20 text-blue-400',
  ARRIBADO_PUERTO: 'bg-amber-500/20 text-amber-400',
  EN_TRANSITO_TERRESTRE: 'bg-purple-500/20 text-purple-400',
  ENTREGADO: 'bg-green-500/20 text-green-400',
  ENTREGADO_PEND_DEMORAS: 'bg-green-500/20 text-green-400',
};

export default function ReportsPage() {
  const { t } = useI18n();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() === 0 ? 12 : now.getMonth()));
  const [year, setYear] = useState(String(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()));

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await previewMonthlyReport({ month, year });
      setReport(res.data);
    } catch {
      toast.error(t('reports.loadError'));
    } finally {
      setLoading(false);
    }
  }, [month, year, t]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleSend = async () => {
    setSending(true);
    try {
      await sendMonthlyReport({ month, year });
      toast.success(t('reports.sentOk'));
    } catch {
      toast.error(t('reports.sendError'));
    } finally {
      setSending(false);
    }
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) years.push(y);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading text-foreground flex items-center gap-2">
              <FileBarChart className="text-primary" size={24} />
              {t('reports.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('reports.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-36" data-testid="report-month-select">
                <Calendar size={14} className="mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((i) => (
                  <SelectItem key={i} value={String(i)}>{t(`reports.months.${i}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-24" data-testid="report-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadReport} disabled={loading} data-testid="report-preview-btn">
              {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
              {t('reports.preview')}
            </Button>
            <Button onClick={handleSend} disabled={sending} data-testid="report-send-btn">
              {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
              {t('reports.sendByEmail')}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : report ? (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: t('reports.metrics.totalShipments'), value: report.total_shipments, icon: Ship, color: 'text-blue-400' },
                { label: t('reports.metrics.containers'), value: report.total_containers, icon: Package, color: 'text-cyan-400' },
                { label: t('reports.metrics.delivered'), value: report.delivered_containers, icon: Award, color: 'text-green-400' },
                { label: t('reports.metrics.active'), value: report.active_shipments, icon: TrendingUp, color: 'text-amber-400' },
                { label: t('reports.metrics.inPort'), value: report.in_port_containers, icon: Ship, color: 'text-orange-400' },
                { label: t('reports.metrics.certsExpiring'), value: report.expiring_certs, icon: ShieldCheck, color: report.expiring_certs > 0 ? 'text-red-400' : 'text-zinc-400' },
              ].map((m, i) => (
                <div key={i} className="bg-card border border-border rounded-lg p-4" data-testid={`report-metric-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <m.icon size={16} className={m.color} />
                    <span className="text-xs text-muted-foreground">{m.label}</span>
                  </div>
                  <p className="text-2xl font-heading">{m.value}</p>
                </div>
              ))}
            </div>

            {/* Month Highlights */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <h3 className="text-sm font-medium mb-2">{t('reports.period')}: {t(`reports.months.${parseInt(month, 10)}`)} {report.year}</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('reports.newShipments')}:</span>
                  <span className="ml-2 font-medium">{report.new_shipments}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('reports.arrivalsThisMonth')}:</span>
                  <span className="ml-2 font-medium">{report.arrivals_this_month || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('reports.deliveriesThisMonth')}:</span>
                  <span className="ml-2 font-medium">{report.deliveries_this_month}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('reports.inTransitToMexico')}:</span>
                  <span className="ml-2 font-medium">{report.in_transit_maritime || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('reports.avgDaysInPort')}:</span>
                  <span className="ml-2 font-medium">{report.avg_transit_days} {t('reports.days')}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Status Breakdown */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3">{t('reports.shipmentsByStatus')}</h3>
                <div className="space-y-2">
                  {Object.entries(report.status_counts || {})
                    .sort(([, a], [, b]) => b - a)
                    .map(([code, count]) => (
                      <div key={code} className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[code] || 'bg-zinc-500/20 text-zinc-300'}`}>
                          {(() => {
                            const k = `statusCodes.${code}`;
                            const lbl = t(k);
                            return lbl !== k ? lbl : code.replace(/_/g, ' ');
                          })()}
                        </span>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* All Importers */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3">{t('reports.importers')}</h3>
                {report.all_importers?.length > 0 ? (
                  <div className="space-y-2">
                    {report.all_importers.map(([name, count], i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm truncate mr-2">{name}</span>
                        <span className="text-sm font-medium text-primary">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('reports.noImportersData')}</p>
                )}
              </div>
            </div>

            {/* Expiring Contracts & Certificates */}
            {(report.expiring_contracts?.length > 0 || report.expiring_certificates?.length > 0) && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3 text-amber-400">{t('reports.upcomingRenewals')}</h3>
                <div className="space-y-2">
                  {report.expiring_contracts?.map((c, i) => (
                    <div key={`c-${i}`} className="flex items-center justify-between text-sm">
                      <span className="text-amber-300">[{t('reports.contractTag')}] {c.product}</span>
                      <span className="text-muted-foreground">{c.expiration_date}</span>
                    </div>
                  ))}
                  {report.expiring_certificates?.map((c, i) => (
                    <div key={`cert-${i}`} className="flex items-center justify-between text-sm">
                      <span className="text-purple-300">[{t('reports.certificateTag')}] {c.product}</span>
                      <span className="text-muted-foreground">{c.expiration_date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
